// components/media/FolderToolbar.tsx
import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { FolderPlus, Trash2, Plus, UploadCloud } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { MediaUploader } from "./MediaUploader";
import { useMediaLibrary } from "@/hooks/use-media-library";
import { MobileFolderToolbar } from "./MobileFolderToolbar";

type FolderToolbarProps = {
  currentPath?: string;
  onCreateFolder: (name: string) => Promise<void> | void;
  onDeleteFolder: () => Promise<void> | void;
  onNavigate: (path: string) => void;
  disableDelete?: boolean;
  mediaState?: ReturnType<typeof useMediaLibrary>;
  onUploadComplete?: () => void;
};

export default function FolderToolbar({
  currentPath,
  onCreateFolder,
  onDeleteFolder,
  onNavigate,
  disableDelete,
  mediaState,
  onUploadComplete,
}: FolderToolbarProps) {
  // UI States
  const [openCreate, setOpenCreate] = useState(false);
  const [openMediaDialog, setOpenMediaDialog] = useState(false);
  const [openDeleteConfirm, setOpenDeleteConfirm] = useState(false);
  const [folderName, setFolderName] = useState("");

  const prevUploadingRef = useRef(false);

  // Path Logic
  const parts = currentPath ? currentPath.split("/").filter(Boolean) : [];
  const crumbs = ["", ...parts.map((_, i) => parts.slice(0, i + 1).join("/"))];

  const handleCreate = async () => {
    if (!folderName.trim()) return;
    await onCreateFolder(folderName.trim());
    setFolderName("");
    setOpenCreate(false);
  };

  // Watch for upload completion
  useEffect(() => {
    if (!mediaState) return;
    const wasUploading = prevUploadingRef.current;
    const isUploading = mediaState.uploading;
    const hasNoFile = mediaState.file === null;

    if (wasUploading && !isUploading && hasNoFile && openMediaDialog) {
      setOpenMediaDialog(false);
      onUploadComplete?.();
    }
    prevUploadingRef.current = isUploading;
  }, [mediaState?.uploading, mediaState?.file, openMediaDialog, onUploadComplete]);

  return (
    <>
      {/* 1. MOBILE VERSION (Visible only on small screens) */}
      <MobileFolderToolbar
        currentPath={currentPath}
        onNavigate={onNavigate}
        onOpenCreateFolder={() => setOpenCreate(true)}
        onOpenUpload={() => setOpenMediaDialog(true)}
        onOpenDelete={() => setOpenDeleteConfirm(true)}
        disableDelete={disableDelete}
      />

      {/* 2. DESKTOP VERSION (Visible only on md and up) */}
      <div className="hidden md:block flex-shrink-0 border-b border-slate-200 bg-white/80 backdrop-blur-sm">
        <div className="flex items-center justify-between gap-4 px-4 py-3">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink
                  className={cn(
                    "cursor-pointer transition-colors hover:text-slate-900",
                    !currentPath && "font-semibold text-slate-900"
                  )}
                  onClick={() => onNavigate("")}
                >
                  Root
                </BreadcrumbLink>
              </BreadcrumbItem>
              {parts.map((p, i) => (
                <div key={i} className="flex items-center">
                  <BreadcrumbSeparator />
                  <BreadcrumbItem>
                    <BreadcrumbLink
                      className={cn(
                        "cursor-pointer transition-colors hover:text-slate-900",
                        i === parts.length - 1 && "font-semibold text-slate-900"
                      )}
                      onClick={() => onNavigate(crumbs[i + 1])}
                    >
                      {p}
                    </BreadcrumbLink>
                  </BreadcrumbItem>
                </div>
              ))}
            </BreadcrumbList>
          </Breadcrumb>

          <div className="flex items-center gap-2">
            {mediaState && (
              <Button
                size="sm"
                className="gap-1.5 border-slate-300"
                onClick={() => setOpenMediaDialog(true)}
              >
                <UploadCloud className="h-4 w-4" />
                New Media
              </Button>
            )}

            <Button
              variant="outline"
              size="sm"
              className="gap-1.5 border-slate-300"
              onClick={() => setOpenCreate(true)}
            >
              <FolderPlus className="h-4 w-4" />
              New Folder
            </Button>

            <Button
              size="sm"
              variant="destructive"
              disabled={disableDelete}
              className="gap-1.5"
              onClick={() => setOpenDeleteConfirm(true)}
            >
              <Trash2 className="h-4 w-4" />
              Delete Folder
            </Button>
          </div>
        </div>
      </div>

      {/* 3. SHARED MODALS (Used by both Mobile & Desktop) */}

      {/* Media Upload Dialog */}
      <Dialog open={openMediaDialog} onOpenChange={setOpenMediaDialog}>
        <DialogContent className="w-full max-w-2xl h-[95dvh] sm:h-auto sm:max-h-[90vh] flex flex-col p-0 gap-0 overflow-hidden">
          <DialogHeader className="px-4 pt-4 pb-3 border-b border-slate-100 shrink-0">
            <DialogTitle>Upload Media</DialogTitle>
            <DialogDescription>
              Upload assets to <b>{currentPath || "Root"}</b>.
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto">
            {mediaState && <MediaUploader state={mediaState} />}
          </div>
        </DialogContent>
      </Dialog>

      {/* Create Folder Dialog (Better for mobile than Popover) */}
      <Dialog open={openCreate} onOpenChange={setOpenCreate}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Create New Folder</DialogTitle>
            <DialogDescription>
              Enter a name for the new folder in {currentPath || "Root"}.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Input
              autoFocus
              value={folderName}
              onChange={(e) => setFolderName(e.target.value)}
              placeholder="e.g. Marketing Assets"
              onKeyDown={(e) => e.key === "Enter" && handleCreate()}
            />
          </div>
          <DialogFooter className="flex-row justify-end gap-2">
            <Button variant="ghost" onClick={() => setOpenCreate(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreate} disabled={!folderName.trim()}>
              <Plus className="mr-2 h-4 w-4" />
              Create Folder
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Alert */}
      <AlertDialog open={openDeleteConfirm} onOpenChange={setOpenDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this folder?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove the folder <b>{currentPath || "Root"}</b> and all its contents.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                onDeleteFolder();
                setOpenDeleteConfirm(false);
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
